import { getLoteStatusOptions, matchesLoteStatus } from "../../lotes/loteStatusOptions";
import ConciliationStatusChip, {
  conciliationStatus,
} from "modules/finops/conciliaciones/components/ConciliationStatusChip";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  CircularProgress,
  Alert,
  Card,
  Box,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh,
  Download,
  Visibility,
  Close,
  PictureAsPdf,
  Add,
  Assessment,
  AttachMoney,
  FilterAlt,
  TableChart,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import { financialStatus } from "./paymentExcel";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import TableDetalleFinanciera from "./TableDetalleFinanciera";
import ModalPDF from "./ModalPDF";
import ModalSubirDocumento from "./Modals/ModalSubirDocumento";
import {
  getConciliationFilesById,
  getPaymentExcel,
} from "modules/finops/conciliaciones/services/financieraService";

import useLoteRecords from "modules/finops/conciliaciones/lotes/hooks/useLoteRecords";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  successDark: "#1e7e34",
  warning: "#da1c15ff",
  info: "#2980b9",
  pdf: "#e74c3c",
  email: "#1a73e8",
  orange: "#e67e22",
  red: "#c0392b",
  redDark: "#a93226",
  excel: "#1e7145",
  excelLight: "#e8f5e9",
};

// ================================================
// HELPERS
// ================================================
const formatFecha = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMoney = (v) =>
  `$${Number(v || 0).toLocaleString("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// ================================================
// COMPONENTES DE CELDA
// ================================================
const LlaveCell = ({ value }) => (
  <Chip
    label={value || "N/A"}
    size="small"
    sx={{
      fontWeight: 600,
      fontFamily: "monospace",
      backgroundColor: value ? `${colors.info}15` : "#f5f5f5",
      color: value ? colors.info : "#999",
      border: value ? `1px solid ${colors.info}30` : "1px solid #ddd",
    }}
  />
);
LlaveCell.propTypes = { value: PropTypes.string };
LlaveCell.defaultProps = { value: "" };

const VerticalCell = ({ value }) => (
  <Chip
    label={value || "N/A"}
    size="small"
    sx={{
      fontWeight: 600,
      backgroundColor: value ? `${colors.success}15` : "#f5f5f5",
      color: value ? colors.success : "#999",
    }}
  />
);
VerticalCell.propTypes = { value: PropTypes.string };
VerticalCell.defaultProps = { value: "" };

const FechaCell = ({ value }) => (
  <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
    {value ? new Date(value).toLocaleDateString() : "N/A"}
  </MDTypography>
);
FechaCell.propTypes = { value: PropTypes.string };
FechaCell.defaultProps = { value: "" };

const ObservacionCell = ({ value }) => (
  <MDTypography variant="caption" sx={{ fontSize: "0.8rem" }}>
    {value || "N/A"}
  </MDTypography>
);
ObservacionCell.propTypes = { value: PropTypes.string };
ObservacionCell.defaultProps = { value: "" };

// ================================================
// WRAPPERS
// ================================================
const LlaveWrapper = ({ row }) => (
  <LlaveCell value={row.original.key_concil || row.original.llave} />
);
LlaveWrapper.propTypes = { row: PropTypes.object.isRequired };

const VerticalWrapper = ({ row }) => (
  <VerticalCell value={row.original.vertical || row.original.realName} />
);
VerticalWrapper.propTypes = { row: PropTypes.object.isRequired };

const StatusWrapper = ({ row }) => <ConciliationStatusChip value={row.original.status} />;
StatusWrapper.propTypes = { row: PropTypes.object.isRequired };

const StatusTextWrapper = ({ row }) => <ConciliationStatusChip value={row.original.status} />;
StatusTextWrapper.propTypes = { row: PropTypes.object.isRequired };

const FechaWrapper = ({ row }) => <FechaCell value={row.original.conciliation_date} />;
FechaWrapper.propTypes = { row: PropTypes.object.isRequired };

const ObservacionWrapper = ({ row }) => <ObservacionCell value={row.original.observation} />;
ObservacionWrapper.propTypes = { row: PropTypes.object.isRequired };

const IdConciliacionWrapper = ({ row }) => (
  <MDTypography variant="caption">
    {row.original.conciliation_id || row.original.id || "N/A"}
  </MDTypography>
);
IdConciliacionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      conciliation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const UuidWrapper = ({ row }) => (
  <MDTypography
    variant="caption"
    sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}
  >
    {row.original.uuid || "N/A"}
  </MDTypography>
);
UuidWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }).isRequired,
};

const FechaCreacionWrapper = ({ row }) => (
  <MDTypography variant="caption" sx={{ fontSize: "0.8rem" }}>
    {formatFecha(row.original.created_at)}
  </MDTypography>
);
FechaCreacionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      created_at: PropTypes.string,
    }),
  }).isRequired,
};

const ConcesionWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600">
    {row.original.concesion || row.original.vertical || "N/A"}
  </MDTypography>
);
ConcesionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vertical: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const NombreWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600" sx={{ fontSize: "0.8rem" }}>
    {String(row.original.name || row.original.realName || "N/A").toLocaleUpperCase("es-CO")}
  </MDTypography>
);
NombreWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      name: PropTypes.string,
      realName: PropTypes.string,
    }),
  }).isRequired,
};

const CountConcilWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600">
    {row.original.countconcil != null
      ? Number(row.original.countconcil).toLocaleString("es-CO")
      : "N/A"}
  </MDTypography>
);
CountConcilWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      countconcil: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const ValorPagoWrapper = ({ row }) => {
  const value = row.original.value_final;
  const pagoDesdeFinops =
    String(row.original.status || "")
      .trim()
      .toLowerCase() === "conciliado-liquidar";
  const available = value != null && String(value).trim() !== "" && Number.isFinite(Number(value));
  return (
    <Typography
      className="financial-payment-value"
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: available ? (pagoDesdeFinops ? "#b91c1c" : "#166534") : "#64748b",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {available ? formatMoney(value) : "Sin definir"}
    </Typography>
  );
};
ValorPagoWrapper.propTypes = { row: PropTypes.object.isRequired };

// ================================================
// COMPONENTE DE ACCIONES
// ================================================
const AccionesCell = ({
  row,
  modo,
  onVerDetalle,
  onVerPDF,
  onAgregarDocumento,
  onFiltrar,
  loadingExcel,
  onDescargarExcel,
}) => {
  const idConcil = row.original.conciliation_id || row.original.id;
  const keyConcil = row.original.key_concil || row.original.llave || "";
  const codeOp = row.original.concesion ?? row.original.code_op ?? "";

  const puedeConsultarArchivos = !!idConcil && String(codeOp).trim() !== "";

  const [files, setFiles] = useState([]);
  const [checkingFiles, setCheckingFiles] = useState(false);

  useEffect(() => {
    if (modo !== "llave" || !puedeConsultarArchivos) {
      setFiles([]);
      setCheckingFiles(false);
      return undefined;
    }

    let active = true;
    setCheckingFiles(true);

    getConciliationFilesById(idConcil, codeOp)
      .then((result) => {
        if (!active) return;
        setFiles(result.success ? result.data || [] : []);
      })
      .catch((error) => {
        console.error("Error verificando archivos:", error);
        if (active) setFiles([]);
      })
      .finally(() => {
        if (active) setCheckingFiles(false);
      });

    return () => {
      active = false;
    };
  }, [modo, idConcil, codeOp, puedeConsultarArchivos]);

  const tieneArchivos = files.length > 0;

  const handleVerPDF = useCallback(() => {
    if (!tieneArchivos) return;
    onVerPDF(row.original, files);
  }, [tieneArchivos, files, onVerPDF, row.original]);

  if (modo === "global") {
    return (
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Tooltip title={keyConcil ? `Filtrar por llave ${keyConcil}` : "Sin llave"}>
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<FilterAlt />}
              onClick={() => keyConcil && onFiltrar && onFiltrar(String(keyConcil).trim())}
              disabled={!keyConcil}
              sx={{
                textTransform: "none",
                bgcolor: colors.primary,
                color: colors.white,
                "&:hover": { bgcolor: "#1a6fa0" },
                fontSize: "0.7rem",
                px: 1.5,
                py: 0.5,
                minWidth: "90px",
              }}
            >
              Filtrar
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Descargar las concesiones autoliquidadas y conciliadas por liquidar">
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={
                loadingExcel ? <CircularProgress size={14} color="inherit" /> : <Download />
              }
              onClick={() => onDescargarExcel?.(row.original)}
              disabled={!keyConcil || loadingExcel}
              sx={{
                textTransform: "none",
                bgcolor: colors.excel,
                color: colors.white,
                "&:hover": { bgcolor: "#155a35" },
                fontSize: "0.7rem",
                px: 1.5,
                py: 0.5,
              }}
            >
              Descargar Excel de pago
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  const compactActionSx = {
    width: 32,
    height: 32,
    borderRadius: "8px",
    border: "1px solid",
    "& .MuiSvgIcon-root": { fontSize: 18 },
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 32px)",
        gap: 0.75,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Tooltip title="Ver detalle de la conciliación" arrow>
        <IconButton
          aria-label="Ver detalle de la conciliación"
          onClick={() => onVerDetalle(row.original)}
          sx={{
            ...compactActionSx,
            color: colors.primary,
            borderColor: "rgba(30,41,59,0.2)",
            bgcolor: "rgba(30,41,59,0.06)",
            "&:hover": { bgcolor: "rgba(30,41,59,0.13)" },
          }}
        >
          <Visibility />
        </IconButton>
      </Tooltip>
      <Tooltip title="Agregar comprobante PDF o imagen" arrow>
        <IconButton
          aria-label="Agregar documento"
          onClick={() => onAgregarDocumento(row.original)}
          sx={{
            ...compactActionSx,
            color: "success.main",
            borderColor: "rgba(46,125,50,0.3)",
            bgcolor: "rgba(46,125,50,0.07)",
            "&:hover": { bgcolor: "rgba(46,125,50,0.14)" },
          }}
        >
          <Add />
        </IconButton>
      </Tooltip>
      {checkingFiles && (
        <Box sx={{ width: 32, height: 32, display: "grid", placeItems: "center" }}>
          <CircularProgress
            size={16}
            aria-label="Consultando documentos"
            sx={{ color: colors.pdf }}
          />
        </Box>
      )}
      {!checkingFiles && tieneArchivos && (
        <Tooltip title={`Ver documentos adjuntos (${files.length})`} arrow>
          <IconButton
            aria-label={`Ver documentos (${files.length})`}
            onClick={handleVerPDF}
            sx={{
              ...compactActionSx,
              color: "error.main",
              borderColor: "rgba(211,47,47,0.3)",
              bgcolor: "rgba(211,47,47,0.07)",
              "&:hover": { bgcolor: "rgba(211,47,47,0.14)" },
            }}
          >
            <PictureAsPdf />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

AccionesCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      has_files: PropTypes.bool,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      conciliation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      files_data: PropTypes.array,
      key_concil: PropTypes.string,
      llave: PropTypes.string,
      concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      code_op: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  modo: PropTypes.oneOf(["global", "llave"]),
  onVerDetalle: PropTypes.func.isRequired,
  onVerPDF: PropTypes.func.isRequired,
  onAgregarDocumento: PropTypes.func.isRequired,
  onFiltrar: PropTypes.func,
  loadingExcel: PropTypes.bool,
  onDescargarExcel: PropTypes.func,
};

AccionesCell.defaultProps = {
  modo: "llave",
  onFiltrar: null,
  loadingExcel: false,
  onDescargarExcel: null,
};

// ================================================
// COMPONENTE PRINCIPAL
// ================================================
const TableLoteFinanciera = ({
  refreshFlag,
  onError,
  onSelectLote,
  onFiltrarLlave,
  llaveSeleccionada = "",
  estadoLoteSeleccionado = "",
  fechaInicio = "",
  fechaFin = "",
  filtroVertical = "",
  filtroBusqueda = "",
  filtroStatus = "todos",
  tipoFiltro = "global",
  onStatusOptionsChange,
  onKeyOptionsChange,
}) => {
  const { allRecords, loading, error, keySummary, fetchRecords, fetchByKey } = useLoteRecords({
    llaveSeleccionada,
    fechaInicio,
    fechaFin,
    filtroVertical,
    filtroStatus: "todos",
    refreshFlag,
    onError,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDetalle, setOpenDetalle] = useState(false);
  const [selectedLoteData, setSelectedLoteData] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedKeyConcil, setSelectedKeyConcil] = useState(null);
  const [selectedFilesData, setSelectedFilesData] = useState([]);
  const [openSubirDocumento, setOpenSubirDocumento] = useState(false);
  const [selectedDocumentData, setSelectedDocumentData] = useState(null);

  const [loadingExcelByRow, setLoadingExcelByRow] = useState({});
  const [downloadNotice, setDownloadNotice] = useState(null);
  const downloads = useRef(new Map());
  useEffect(
    () => () => {
      downloads.current.forEach((controller) => controller.abort());
      downloads.current.clear();
    },
    []
  );
  const isKeyMode = !!(llaveSeleccionada && String(llaveSeleccionada).trim() !== "");
  const isImportedKey = isKeyMode && financialStatus(estadoLoteSeleccionado) === "en-proceso-lote";

  useEffect(() => {
    setPage(0);
  }, [llaveSeleccionada, fechaInicio, fechaFin, filtroVertical, filtroBusqueda, filtroStatus]);

  // ================================================
  // FILTRADO EN CLIENTE
  // ================================================
  const searchRecords = useMemo(() => {
    let data = [...allRecords];
    data = data.filter((item) =>
      isKeyMode
        ? isImportedKey ||
          ["auto-liquidado", "conciliado-liquidar", "pagado"].includes(financialStatus(item.status))
        : ["Por liquidar", "En Proceso", "Lote en proceso"].includes(
            conciliationStatus(item.status).label
          )
    );

    if (filtroBusqueda && filtroBusqueda.trim() !== "") {
      const searchTerm = filtroBusqueda.toLowerCase().trim();
      data = data.filter((item) => {
        const searchableFields = [
          String(item.id || ""),
          String(item.uuid || ""),
          String(item.conciliation_id || ""),
          String(item.name || item.realName || ""),
          String(item.key_concil || ""),
          String(item.concesion || ""),
          String(item.status || ""),
        ];
        return searchableFields.some((field) => field.toLowerCase().includes(searchTerm));
      });
    }

    return data;
  }, [allRecords, filtroBusqueda, isKeyMode, isImportedKey]);
  const statusOptions = useMemo(() => getLoteStatusOptions(searchRecords), [searchRecords]);
  useEffect(() => {
    if (!loading) onStatusOptionsChange?.(statusOptions);
  }, [loading, statusOptions, onStatusOptionsChange]);
  const filteredRecords = useMemo(
    () => searchRecords.filter((item) => matchesLoteStatus(item.status, filtroStatus)),
    [searchRecords, filtroStatus]
  );

  const availableKeys = useMemo(() => {
    const keys = new Map();
    filteredRecords.forEach((row) => {
      const value = String(row.key_concil || row.llave || "").trim();
      if (value && !keys.has(value))
        keys.set(value, { value, label: value, id: row.id, status: row.status });
    });
    return [...keys.values()];
  }, [filteredRecords]);
  useEffect(() => {
    if (!isKeyMode) onKeyOptionsChange?.({ options: availableKeys, loading });
  }, [availableKeys, loading, isKeyMode, onKeyOptionsChange]);

  const totalValorPagar = useMemo(
    () =>
      filteredRecords.reduce((total, item) => {
        const value = Number(item.value_final ?? item.valor_concesion);
        return total + (Number.isFinite(value) ? Math.round(value * 100) : 0);
      }, 0) / 100,
    [filteredRecords]
  );

  const totalRecords = filteredRecords.length;

  // ================================================
  // HANDLERS
  // ================================================
  const handlePaginationChange = useCallback(({ pageIndex, pageSize }) => {
    setPage(pageIndex);
    setRowsPerPage(pageSize);
  }, []);

  const handleVerDetalle = useCallback(
    (row) => {
      setSelectedLoteData(row);
      setOpenDetalle(true);
      if (onSelectLote) onSelectLote(row.conciliation_id || row.id, row.concesion);
    },
    [onSelectLote]
  );

  const handleFiltrar = useCallback(
    (key) => {
      if (!key) return;

      if (onFiltrarLlave) {
        onFiltrarLlave(key);
      } else {
        fetchByKey(key);
      }
    },
    [onFiltrarLlave, fetchByKey]
  );

  const handleCloseDetalle = useCallback(() => {
    setOpenDetalle(false);
    setSelectedLoteData(null);
  }, []);

  const handleVerPDF = useCallback((row, files) => {
    const keyConcil = row.key_concil || row.llave;
    setSelectedKeyConcil(keyConcil);
    setSelectedFilesData(files);
    setOpenPDFModal(true);
  }, []);

  const handleClosePDFModal = useCallback(() => {
    setOpenPDFModal(false);
    setSelectedKeyConcil(null);
    setSelectedFilesData([]);
  }, []);

  const handleAgregarDocumento = useCallback((row) => {
    setSelectedDocumentData(row);
    setOpenSubirDocumento(true);
  }, []);

  const handleCloseSubirDocumento = useCallback(() => {
    setOpenSubirDocumento(false);
    setSelectedDocumentData(null);
  }, []);

  const handleImportSuccess = useCallback(() => {
    setOpenSubirDocumento(false);
    setSelectedDocumentData(null);
    if (llaveSeleccionada && String(llaveSeleccionada).trim() !== "") {
      fetchByKey(String(llaveSeleccionada).trim());
    } else {
      fetchRecords();
    }
  }, [fetchByKey, fetchRecords, llaveSeleccionada]);

  const handleDescargarExcelPago = useCallback(async (row) => {
    const key = String(row.key_concil || row.llave || "").trim();
    if (!key || downloads.current.has(key)) return;
    const controller = new AbortController();
    downloads.current.set(key, controller);
    setLoadingExcelByRow((prev) => ({ ...prev, [key]: true }));
    setDownloadNotice(null);
    try {
      const { file, filename } = await getPaymentExcel(key, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      try {
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      if (!controller.signal.aborted)
        setDownloadNotice({
          message: err.message || "No se pudo descargar el Excel de pago.",
          severity: err.severity || "error",
        });
    } finally {
      downloads.current.delete(key);
      if (!controller.signal.aborted) setLoadingExcelByRow((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  // ================================================
  // EXPORTAR EXCEL
  // ================================================
  const exportToExcel = useCallback(() => {
    if (filteredRecords.length === 0) return;

    try {
      let exportData;
      let cols;

      if (isKeyMode) {
        exportData = filteredRecords.map((item) => ({
          ID: item.conciliation_id || item.id,
          Concesión: item.concesion,
          Nombre: item.name || item.realName,
          Vertical: item.vertical,
          "Fecha Conciliación": item.conciliation_date,
          "Key Concil": item.key_concil || "N/A",
          "Total Registros": item.countconcil,
          "Valor a pagar (COP)": item.value_final ?? item.valor_concesion ?? "",
          Status: conciliationStatus(item.status).label,
          Observación: item.observation,
          Creado: item.created_at ? new Date(item.created_at).toLocaleString() : "N/A",
        }));
        cols = [
          { wch: 10 },
          { wch: 12 },
          { wch: 30 },
          { wch: 15 },
          { wch: 18 },
          { wch: 20 },
          { wch: 15 },
          { wch: 20 },
          { wch: 12 },
          { wch: 40 },
          { wch: 22 },
        ];
      } else {
        exportData = filteredRecords.map((item) => ({
          ID: item.id,
          UUID: item.uuid || "N/A",
          Estado: conciliationStatus(item.status).label,
          Llave: item.key_concil || "N/A",
          "Fecha Creación": formatFecha(item.created_at),
        }));
        cols = [{ wch: 8 }, { wch: 40 }, { wch: 14 }, { wch: 16 }, { wch: 22 }];
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws["!cols"] = cols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Conciliaciones");
      XLSX.writeFile(wb, `conciliaciones_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Error al exportar:", err);
    }
  }, [filteredRecords, isKeyMode]);

  // ================================================
  // COLUMNAS Y FILAS
  // ================================================
  const AccionesCellWithHandlers = useCallback(
    ({ row }) => {
      const keyConcil = row.original.key_concil || row.original.llave || "";

      return (
        <AccionesCell
          row={row}
          modo={isKeyMode ? "llave" : "global"}
          onVerDetalle={handleVerDetalle}
          onVerPDF={handleVerPDF}
          onAgregarDocumento={handleAgregarDocumento}
          onFiltrar={handleFiltrar}
          loadingExcel={!!loadingExcelByRow[keyConcil]}
          onDescargarExcel={handleDescargarExcelPago}
        />
      );
    },
    [
      isKeyMode,
      handleVerDetalle,
      handleVerPDF,
      handleAgregarDocumento,
      handleFiltrar,
      loadingExcelByRow,
      handleDescargarExcelPago,
    ]
  );

  AccionesCellWithHandlers.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const globalColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: "6%", Cell: IdConciliacionWrapper },
      { Header: "UUID", accessor: "uuid", width: "28%", Cell: UuidWrapper },
      { Header: "Estado", accessor: "status", width: "16%", Cell: StatusTextWrapper },
      { Header: "Llave", accessor: "key_concil", width: "16%", Cell: LlaveWrapper },
      {
        Header: "Fecha Creación",
        accessor: "created_at",
        width: "18%",
        Cell: FechaCreacionWrapper,
      },
      { Header: "Acciones", accessor: "acciones", width: "16%", Cell: AccionesCellWithHandlers },
    ],
    [AccionesCellWithHandlers]
  );

  const keyColumns = useMemo(
    () => [
      {
        Header: "Código",
        accessor: "concesion",
        width: "16.666%",
        Cell: ConcesionWrapper,
        align: "center",
      },
      {
        Header: "Concesión",
        accessor: "name",
        width: "16.666%",
        Cell: NombreWrapper,
        align: "center",
      },
      {
        Header: "Registros",
        accessor: "countconcil",
        width: "16.666%",
        Cell: CountConcilWrapper,
        align: "center",
      },
      {
        Header: "Valor a pagar",
        accessor: "value_final",
        width: "16.666%",
        Cell: ValorPagoWrapper,
        align: "center",
      },
      {
        Header: "Estado",
        accessor: "status",
        width: "16.666%",
        Cell: StatusWrapper,
        align: "center",
      },
      {
        Header: "Acciones",
        accessor: "acciones",
        width: "16.666%",
        Cell: AccionesCellWithHandlers,
        align: "center",
      },
    ],
    [AccionesCellWithHandlers]
  );

  const importedColumns = useMemo(
    () =>
      keyColumns
        .filter((column) => !["value_final", "status"].includes(column.accessor))
        .map((column) => ({ ...column, width: "25%" })),
    [keyColumns]
  );
  const columns = isKeyMode ? (isImportedKey ? importedColumns : keyColumns) : globalColumns;

  const rows = useMemo(
    () =>
      filteredRecords.map((item) => ({
        id: item.conciliation_id || item.id,
        conciliation_id: item.conciliation_id || item.id,
        detailReference: item.detailReference,
        uuid: item.uuid,
        concesion: item.concesion,
        name: item.name || item.realName,
        realName: item.realName,
        vertical: item.vertical,
        conciliation_date: item.conciliation_date,
        key_concil: item.key_concil,
        countconcil: item.countconcil,
        value_final: item.value_final ?? item.valor_concesion,
        status: item.status,
        estado_cuadre: item.estado_cuadre,
        observation: item.observation,
        created_at: item.created_at,
        original: item,
      })),
    [filteredRecords]
  );

  // ================================================
  // RENDER
  // ================================================
  return (
    <>
      {downloadNotice && (
        <Alert
          severity={downloadNotice.severity}
          onClose={() => setDownloadNotice(null)}
          sx={{ mb: 2 }}
        >
          {downloadNotice.message}
        </Alert>
      )}
      <MDBox>
        <Card
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          <MDBox
            p={3}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  {isKeyMode ? "Concesiones de la llave" : "Lotes financiera"}
                </MDTypography>
                <MDTypography variant="body2" color="white" opacity={0.8}>
                  {totalRecords.toLocaleString("es-CO")} {isKeyMode ? "concesiones" : "lotes"}
                  {filtroBusqueda && (
                    <span style={{ marginLeft: 8 }}>Búsqueda: {filtroBusqueda}</span>
                  )}
                  {llaveSeleccionada && (
                    <span style={{ marginLeft: 8 }}>Llave: {llaveSeleccionada}</span>
                  )}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                  <MDButton
                    variant="contained"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    disabled={filteredRecords.length === 0}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: colors.white,
                      color: colors.primary,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                      "&:disabled": {
                        bgcolor: "rgba(255,255,255,0.3)",
                        color: "rgba(255,255,255,0.5)",
                      },
                    }}
                  >
                    Exportar Excel
                  </MDButton>
                  <MDButton
                    variant="contained"
                    onClick={() => {
                      if (llaveSeleccionada && String(llaveSeleccionada).trim() !== "") {
                        fetchByKey(String(llaveSeleccionada).trim());
                      } else {
                        fetchRecords();
                      }
                    }}
                    disabled={loading}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: colors.white,
                      color: colors.primary,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    }}
                  >
                    {loading ? "Cargando..." : "Actualizar"}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {isKeyMode && keySummary && (
            <Box
              sx={{
                px: 3,
                py: 2.5,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: isImportedKey ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {[
                  [
                    isImportedKey ? "Cantidad de pasos" : "Registros conciliados",
                    keySummary.totalRegistros.toLocaleString("es-CO"),
                    "#2563eb",
                    "rgba(37,99,235,0.08)",
                  ],
                  [
                    isImportedKey ? "Total a pagar" : "Valor total",
                    formatMoney(isImportedKey ? keySummary.valorTotal : totalValorPagar),
                    "#15803d",
                    "rgba(21,128,61,0.08)",
                  ],
                  ...(isImportedKey
                    ? [
                        [
                          "Novedades · Abonos",
                          formatMoney(keySummary.totalAbonoNov),
                          "#0f766e",
                          "rgba(15,118,110,0.08)",
                        ],
                        [
                          "Novedades · Ajustes",
                          formatMoney(keySummary.totalAjusteNov),
                          "#b45309",
                          "rgba(180,83,9,0.08)",
                        ],
                      ]
                    : []),
                ].map(([label, value, accent, tint]) => (
                  <Box
                    key={label}
                    sx={{
                      px: 2,
                      py: 1.5,
                      border: 1,
                      borderColor: accent,
                      borderTop: "3px solid",
                      borderTopColor: accent,
                      borderRadius: 2,
                      bgcolor: tint,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "text.primary", fontWeight: 500, mb: 0.75 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "text.primary",
                        fontVariantNumeric: "tabular-nums",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {keySummary.conciliationId && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5 }}>
                  ID de conciliación: {keySummary.conciliationId}
                </Typography>
              )}
            </Box>
          )}
          {loading ? (
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              gap={2}
              p={3}
            >
              <CircularProgress sx={{ color: colors.secondary }} />
              <MDTypography variant="h6" color="text.secondary" fontWeight="bold">
                Cargando conciliaciones...
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                maxWidth={460}
              >
                ⏱️ Este proceso puede tardar hasta <strong>3 minutos</strong> en responder.
                <br />
                Por favor <strong>no cierres ni recargues la página</strong> mientras se completa la
                consulta.
              </MDTypography>
              <Chip
                icon={<CircularProgress size={14} sx={{ color: "#fff !important" }} />}
                label="Procesando en el servidor"
                sx={{
                  bgcolor: colors.info,
                  color: "#fff",
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 2 },
                }}
              />
            </MDBox>
          ) : error ? (
            <Alert severity="error" sx={{ m: 3 }}>
              <MDTypography variant="body2" fontWeight="bold">
                Error al cargar los datos
              </MDTypography>
              <MDTypography variant="caption">{error}</MDTypography>
            </Alert>
          ) : totalRecords === 0 ? (
            <MDBox p={5} textAlign="center">
              <MDTypography variant="h6" sx={{ color: "#64748b" }}>
                {filtroBusqueda
                  ? "No se encontraron resultados"
                  : llaveSeleccionada
                  ? `No se encontraron conciliaciones para la llave ${llaveSeleccionada}`
                  : "No hay conciliaciones disponibles"}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
                {filtroBusqueda
                  ? `No se encontraron coincidencias para "${filtroBusqueda}"`
                  : llaveSeleccionada
                  ? "Verifique que la llave seleccionada tenga conciliaciones registradas"
                  : "Ajuste los filtros para ver resultados"}
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <Box
                sx={{
                  "& thead th": {
                    bgcolor: "background.default",
                    py: 1.5,
                    ...(isKeyMode ? { width: isImportedKey ? "25%" : "16.666%", px: 1.5 } : {}),
                  },
                  "& thead th > div": { opacity: 1, color: "text.primary" },
                  "& tbody td": {
                    py: 1.5,
                    px: isKeyMode ? 1.5 : 2,
                    verticalAlign: "middle",
                    ...(isKeyMode ? { textAlign: "center" } : {}),
                  },
                  "& tbody td .MuiTypography-root:not(.financial-payment-value)": {
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#f1f5f9 !important" : "#111827 !important",
                    fontWeight: "600 !important",
                    opacity: "1 !important",
                  },
                  ...(isKeyMode
                    ? {
                        "& tbody td > div": { width: "100%", maxWidth: "100%" },
                        "& tbody td > div > .MuiTypography-root:not(.financial-payment-value)": {
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#f1f5f9 !important"
                              : "#111827 !important",
                          fontWeight: "600 !important",
                          opacity: "1 !important",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                        "& tbody td:last-child > div > div": { justifyContent: "center", gap: 1 },
                        "& tbody td:last-child button": {
                          minHeight: 34,
                          borderRadius: 2,
                          boxShadow: "none",
                          textTransform: "none",
                        },
                      }
                    : {}),
                  "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" },
                  "& tbody td span, & tbody td p": { fontSize: "13px" },
                  "& table": {
                    minWidth: isKeyMode ? (isImportedKey ? 800 : 1200) : 1080,
                    ...(isKeyMode ? { tableLayout: "fixed", width: "100%" } : {}),
                  },
                }}
              >
                <DataTable
                  key={
                    isKeyMode
                      ? isImportedKey
                        ? "tabla-lote-importado"
                        : "tabla-llave"
                      : "tabla-global"
                  }
                  table={{ columns, rows }}
                  isSorted={true}
                  onPaginationChange={handlePaginationChange}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={false}
                  pagination={{ variant: "outlined", color: "info" }}
                  paginationResetKey={JSON.stringify([
                    llaveSeleccionada,
                    fechaInicio,
                    fechaFin,
                    filtroVertical,
                    filtroStatus,
                    filtroBusqueda,
                    refreshFlag,
                  ])}
                />
              </Box>
            </>
          )}
        </Card>
      </MDBox>

      {/* MODAL DETALLE */}
      <Dialog
        open={openDetalle}
        onClose={handleCloseDetalle}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
            overflow: "hidden",
            width: "95%",
            maxWidth: "95%",
            height: "auto",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 4,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <MDTypography variant="h5" fontWeight="bold">
              Detalle de Conciliación:{" "}
              {selectedLoteData?.name || selectedLoteData?.realName || selectedLoteData?.id}
            </MDTypography>
            {(selectedLoteData?.concesion || selectedLoteData?.vertical) && (
              <Chip
                label={selectedLoteData?.concesion || selectedLoteData?.vertical}
                size="medium"
                sx={{
                  fontWeight: 600,
                  backgroundColor: `${colors.success}15`,
                  color: colors.success,
                  fontSize: "0.9rem",
                }}
              />
            )}
            {selectedLoteData?.key_concil && (
              <Chip
                label={`Llave: ${selectedLoteData.key_concil}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  backgroundColor: `${colors.info}15`,
                  color: colors.info,
                  fontFamily: "monospace",
                }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <MDTypography variant="caption" color="text.secondary">
              ID: {selectedLoteData?.conciliation_id || selectedLoteData?.id}
            </MDTypography>
            <IconButton onClick={handleCloseDetalle} size="large">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "auto" }}>
          <TableDetalleFinanciera
            conciliationId={selectedLoteData?.conciliation_id || selectedLoteData?.id}
            status={selectedLoteData?.status}
            valorPagoFinops={selectedLoteData?.value_final}
            detailReference={selectedLoteData?.detailReference}
            concesion={selectedLoteData?.concesion}
            llaveConciliacion={selectedLoteData?.key_concil || selectedLoteData?.llave || null}
          />
        </DialogContent>
      </Dialog>

      <ModalPDF
        open={openPDFModal}
        onClose={handleClosePDFModal}
        keyConcil={selectedKeyConcil}
        filesData={selectedFilesData}
      />

      <ModalSubirDocumento
        open={openSubirDocumento}
        onClose={handleCloseSubirDocumento}
        onImportSuccess={handleImportSuccess}
        onError={onError}
        idConcil={selectedDocumentData?.conciliation_id || selectedDocumentData?.id}
        keyConcil={selectedDocumentData?.key_concil || selectedDocumentData?.llave}
        code={selectedDocumentData?.concesion}
        conciliationName={selectedDocumentData?.name || selectedDocumentData?.realName}
        vertical={selectedDocumentData?.vertical || selectedDocumentData?.concesion}
      />
    </>
  );
};

TableLoteFinanciera.propTypes = {
  estadoLoteSeleccionado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onKeyOptionsChange: PropTypes.func,
  onStatusOptionsChange: PropTypes.func,
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  onSelectLote: PropTypes.func,
  onFiltrarLlave: PropTypes.func,
  llaveSeleccionada: PropTypes.string,
  fechaInicio: PropTypes.string,
  fechaFin: PropTypes.string,
  filtroVertical: PropTypes.string,
  filtroBusqueda: PropTypes.string,
  filtroStatus: PropTypes.string,
  tipoFiltro: PropTypes.string,
};

TableLoteFinanciera.defaultProps = {
  estadoLoteSeleccionado: "",
  refreshFlag: false,
  onError: null,
  onSelectLote: null,
  onFiltrarLlave: null,
  llaveSeleccionada: "",
  fechaInicio: "",
  fechaFin: "",
  filtroVertical: "",
  filtroBusqueda: "",
  filtroStatus: "todos",
  tipoFiltro: "global",
};

export default TableLoteFinanciera;
